import React, { useState } from "react";
import { User, Radar, Send, Flag } from "lucide-react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAppContext } from "../context/AppContext";

export default function LocalChatTab({
  displayedChatMessages,
  isLocating,
  userCoords,
  setSelectedChatUser,
  handleReport,
  chatEndRef,
}: any) {
  const { lang, t, user, activePet } = useAppContext();

  const [chatInput, setChatInput] = useState("");
  const [lastMessageTime, setLastMessageTime] = useState(0);
  const [reactionPopup, setReactionPopup] = useState<string | number | null>(
    null
  );

  const handleSendLocalMessage = async () => {
    if (!chatInput.trim() || !user) return;
    if (Date.now() - lastMessageTime < 3000) {
      alert(
        lang === "uk"
          ? "⏳ Не так швидко! Зачекайте пару секунд."
          : "⏳ Not so fast! Wait a few seconds."
      );
      return;
    }
    const text = chatInput;
    setChatInput("");
    const points = parseInt(
      localStorage.getItem(`mypaws_pts_${user.uid}`) || "0"
    );
    let userRankIcon =
      points >= 300 ? "🥇" : points >= 150 ? "🥈" : points >= 50 ? "🥉" : "🌱";
    await addDoc(collection(db, "local_chat"), {
      author: activePet.name,
      text,
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
      userId: user.uid,
      petPhoto: activePet.photo || null,
      rankIcon: userRankIcon,
      coords: userCoords,
      breed: activePet.breed || "",
      gender: activePet.gender || "",
      dob: activePet.dob || "",
      createdAt: serverTimestamp(),
    });
    setLastMessageTime(Date.now());
    setTimeout(
      () =>
        chatEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        }),
      100
    );
  };

  const handleReaction = async (msgId: string | number, emoji: string) => {
    if (!user) return;
    const msgRef = doc(db, "local_chat", String(msgId));
    const msgSnap = await getDoc(msgRef);
    if (!msgSnap.exists()) return;
    const data = msgSnap.data();
    const currentReactions = data.reactions || {};
    const usersForEmoji = currentReactions[emoji] || [];
    if (usersForEmoji.includes(user.uid)) {
      currentReactions[emoji] = usersForEmoji.filter(
        (id: string) => id !== user.uid
      );
      if (currentReactions[emoji].length === 0) delete currentReactions[emoji];
    } else {
      currentReactions[emoji] = [...usersForEmoji, user.uid];
    }
    await updateDoc(msgRef, { reactions: currentReactions });
    setReactionPopup(null);
  };

  return (
    <>
      <div
        className="flex-1 overflow-y-auto px-4 pt-2 flex flex-col gap-6 no-scrollbar pb-32"
        onClick={() => setReactionPopup(null)}
      >
        {isLocating ? (
          <div className="flex-1 flex flex-col items-center justify-center text-[var(--text-muted)] font-bold">
            <Radar size={48} className="animate-ping mb-4 text-[#a073e7]" />
          </div>
        ) : (
          /* НАДІЙНИЙ ЗАПОБІЖНИК ТУТ */
          (displayedChatMessages || []).map((msg: any) => (
            <div
              key={msg.id}
              className={`flex w-full ${
                msg.isMe ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`flex gap-2 max-w-[85%] ${
                  msg.isMe ? "flex-row-reverse" : "flex-row"
                }`}
              >
                <div className="flex flex-col items-center shrink-0">
                  <div className="flex items-center gap-1 mb-1 whitespace-nowrap h-[14px]">
                    <span className="text-[10px] text-[var(--text-muted)] font-bold">
                      {msg.author}
                    </span>
                    {msg.rankIcon && (
                      <span className="text-[10px]">{msg.rankIcon}</span>
                    )}
                  </div>
                  <div
                    className="w-10 h-10 rounded-full bg-[var(--accent-soft)] border-2 border-[var(--bg-card)] shadow-sm flex items-center justify-center font-bold text-[#a073e7] overflow-hidden cursor-pointer active:scale-95 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedChatUser({
                        id: msg.userId,
                        name: msg.author,
                        photo: msg.petPhoto,
                        breed: msg.breed,
                        gender: msg.gender,
                        dob: msg.dob,
                      });
                    }}
                  >
                    {msg.petPhoto ? (
                      <img
                        src={msg.petPhoto}
                        className="w-full h-full object-cover"
                        alt="pet"
                      />
                    ) : (
                      <User size={20} />
                    )}
                  </div>
                </div>
                <div
                  className={`flex flex-col pt-[18px] ${
                    msg.isMe ? "items-end" : "items-start"
                  } relative group`}
                >
                  <div className="relative">
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setReactionPopup(
                          reactionPopup === msg.id ? null : msg.id
                        );
                      }}
                      className={`px-4 py-2.5 shadow-sm text-[15px] cursor-pointer ${
                        msg.isMe
                          ? "bg-[#a073e7] text-white rounded-l-2xl rounded-tr-2xl"
                          : "bg-[var(--bg-card)] border border-[var(--border-color)] text-[var(--text-main)] rounded-r-2xl rounded-tl-2xl"
                      }`}
                    >
                      {msg.text}
                    </div>
                    {!msg.isMe && (
                      <button
                        onClick={() =>
                          handleReport("chat", String(msg.id), msg.userId || "")
                        }
                        className="absolute -right-8 top-2 text-[var(--text-muted)] opacity-0 group-hover:opacity-100 hover:text-[var(--red-text)] transition-all"
                      >
                        <Flag size={14} />
                      </button>
                    )}
                    {reactionPopup === msg.id && (
                      <div
                        className={`absolute top-[-40px] ${
                          msg.isMe ? "right-0" : "left-0"
                        } bg-[var(--bg-card)] border border-[var(--border-color)] shadow-lg rounded-full px-2 py-1 flex gap-2 z-50 animate-fade-in`}
                      >
                        {["👍", "❤️", "😂", "😢", "🔥"].map((emoji) => (
                          <button
                            key={emoji}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReaction(msg.id, emoji);
                            }}
                            className="hover:scale-125 transition-transform text-lg"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                    <div
                      className={`flex flex-wrap gap-1 mt-1 ${
                        msg.isMe ? "justify-end" : "justify-start"
                      }`}
                    >
                      {Object.entries(msg.reactions).map(
                        ([emoji, users]: [string, any]) => (
                          <span
                            key={emoji}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReaction(msg.id, emoji);
                            }}
                            className={`text-[10px] px-1.5 py-0.5 rounded-full border cursor-pointer ${
                              users.includes(user?.uid || "")
                                ? "bg-[var(--accent-soft)] border-[var(--border-accent)] text-[#a073e7]"
                                : "bg-[var(--bg-card)] border-[var(--border-color)] text-[var(--text-muted)]"
                            }`}
                          >
                            {emoji} {users.length}
                          </span>
                        )
                      )}
                    </div>
                  )}
                  <span className="text-[9px] text-[var(--text-muted)] mt-1">
                    {msg.time}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={chatEndRef} className="h-1 shrink-0" />
      </div>

      <div className="fixed bottom-[80px] left-0 right-0 bg-[var(--bg-card)] p-3 border-t border-[var(--border-color)] z-30">
        <div className="flex items-center gap-2 bg-[var(--bg-soft)] border border-[var(--border-accent)] rounded-full px-1 py-1">
          <input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSendLocalMessage()}
            placeholder={t.writeAll}
            className="flex-1 bg-transparent px-4 outline-none text-sm h-10 text-[var(--text-main)]"
          />
          <button
            onClick={handleSendLocalMessage}
            className="p-2.5 bg-[#a073e7] rounded-full text-white shadow-md active:scale-95"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </>
  );
}
